import type { ReactNode } from 'react';
import type { BillingPeriod } from './BillingPeriodToggle';

export type SubscriptionTier = 'free' | 'gold' | 'platinum';

interface SubscriptionFeature {
  text: string;
  included: boolean;
  icon?: string;
  iconClassName?: string;
}

interface PriceInfo {
  price: string;
  period: string;
}

interface SubscriptionCardContent {
  tier: SubscriptionTier;
  title: string;
  badgeText: string;
  tagline: string;
  prices: Record<BillingPeriod, PriceInfo>;
  showShine?: boolean;
  titleClassName?: string;
  priceClassName?: string;
  features: SubscriptionFeature[];
}

const SUBSCRIPTION_CARD_CONTENT: Record<SubscriptionTier, SubscriptionCardContent> = {
  free: {
    tier: 'free',
    title: 'Free',
    badgeText: 'FREE',
    tagline: 'Perfect to get started',
    prices: {
      daily: { price: '$0', period: 'forever' },
      monthly: { price: '$0', period: 'forever' },
      yearly: { price: '$0', period: 'forever' },
    },
    features: [
      { text: '10 arbitrage searches/day', included: true },
      { text: 'Unlimited +EV opportunities', included: true },
      { text: 'No live game data', included: false },
      { text: 'No player props', included: false },
    ],
  },
  gold: {
    tier: 'gold',
    title: 'Gold',
    badgeText: '⚡ MOST POPULAR',
    tagline: 'For serious bettors',
    prices: {
      daily: { price: '$4.99', period: '/day' },
      monthly: { price: '$29.99', period: '/month' },
      yearly: { price: '$249.99', period: '/year' },
    },
    showShine: true,
    titleClassName: 'subscription-tier-gold',
    priceClassName: 'subscription-price-gold',
    features: [
      {
        text: 'Unlimited arbitrage searches',
        included: true,
        iconClassName: 'subscription-icon-gold',
      },
      {
        text: 'Unlimited +EV opportunities',
        included: true,
        iconClassName: 'subscription-icon-gold',
      },
      {
        text: 'Live game data access',
        included: true,
        iconClassName: 'subscription-icon-gold',
      },
      {
        text: '1-click bet placement',
        included: true,
        iconClassName: 'subscription-icon-gold',
      },
      { text: 'No player props', included: false },
      { text: 'No auto-refresh', included: false },
    ],
  },
  platinum: {
    tier: 'platinum',
    title: 'Platinum',
    badgeText: '👑 PREMIUM',
    tagline: 'Maximum profit potential',
    prices: {
      daily: { price: '$14.99', period: '/day' },
      monthly: { price: '$89.99', period: '/month' },
      yearly: { price: '$849.99', period: '/year' },
    },
    showShine: true,
    titleClassName: 'subscription-tier-platinum',
    priceClassName: 'subscription-price-platinum',
    features: [
      {
        text: 'Unlimited arbitrage searches',
        included: true,
        iconClassName: 'subscription-icon-platinum',
      },
      {
        text: 'Unlimited +EV opportunities',
        included: true,
        iconClassName: 'subscription-icon-platinum',
      },
      {
        text: 'Live game data access',
        included: true,
        iconClassName: 'subscription-icon-platinum',
      },
      {
        text: '1-click bet placement',
        included: true,
        iconClassName: 'subscription-icon-platinum',
      },
      {
        text: 'Player prop opportunities',
        included: true,
        iconClassName: 'subscription-icon-platinum',
      },
      {
        text: 'Auto-refresh odds',
        included: true,
        iconClassName: 'subscription-icon-platinum',
      },
    ],
  },
};

const TIER_ORDER: SubscriptionTier[] = ['free', 'gold', 'platinum'];

type PriceOverride = number | string;

interface SubscriptionTierCardsProps {
  renderCTA: (tier: SubscriptionTier) => ReactNode;
  priceOverrides?: Partial<Record<SubscriptionTier, PriceOverride>>;
  billingPeriod?: BillingPeriod;
  className?: string;
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default function SubscriptionTierCards({
  renderCTA,
  priceOverrides,
  billingPeriod = 'monthly',
  className,
}: SubscriptionTierCardsProps) {
  return (
    <div className={`subscription-tiers-grid${className ? ` ${className}` : ''}`}>
      {TIER_ORDER.map((tier) => {
        const content = SUBSCRIPTION_CARD_CONTENT[tier];
        const priceInfo = content.prices[billingPeriod];
        const priceOverride = priceOverrides?.[tier];
        const price =
          typeof priceOverride === 'number'
            ? `$${priceOverride.toFixed(2)}`
            : priceOverride || priceInfo.price;

        return (
          <div key={tier} className={`subscription-card subscription-card-${tier}`}>
            <div className={`subscription-badge subscription-badge-${tier}`}>
              {content.badgeText}
            </div>

            {content.showShine && <div className="subscription-shine-effect"></div>}

            <div className="subscription-header">
              <h3
                className={`subscription-tier-name${content.titleClassName ? ` ${content.titleClassName}` : ''
                  }`}
              >
                {content.title}
              </h3>
              <div className="subscription-price-container">
                <span
                  className={`subscription-price${content.priceClassName ? ` ${content.priceClassName}` : ''
                    }`}
                >
                  {price}
                </span>
                <span className="subscription-period">{priceInfo.period}</span>
              </div>
              <p className="subscription-tagline">{content.tagline}</p>
            </div>

            <ul className="subscription-features">
              {content.features.map((feature) => (
                <li
                  key={feature.text}
                  className={`subscription-feature ${feature.included ? 'subscription-feature-included' : 'subscription-feature-excluded'
                    }`}
                >
                  <span
                    className={`subscription-feature-icon${feature.iconClassName ? ` ${feature.iconClassName}` : ''
                      }`}
                  >
                    {feature.icon ? (
                      feature.icon
                    ) : feature.included ? (
                      <CheckIcon />
                    ) : (
                      <XIcon />
                    )}
                  </span>
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>

            {renderCTA(tier)}
          </div>
        );
      })}
    </div>
  );
}
